/* =========================================================================
   CashAppPayout.tsx — Create Cash App payouts + view payout history
   ========================================================================= */
import React, { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, Send, Search, Download } from "lucide-react";
import { format } from "date-fns";

const CASHAPP_BASE = "https://api.ibitplay.com/cashApp";

const authHeaders = (json = false): Record<string, string> => {
  const token = localStorage.getItem("token");
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

interface PayoutRow {
  id: number;
  order_no: string;
  account_no: string;
  amount: string | number;
  currency: string;
  status: "pending" | "success" | "failed" | string;
  failure_reason: string | null;
  created_at: string;
  updated_at: string;
}

const fmt = (iso: string) => {
  try { return format(new Date(iso), "yyyy-MM-dd HH:mm:ss"); }
  catch { return iso; }
};

const statusClass = (s: string) => {
  if (s === "success") return "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200";
  if (s === "failed") return "bg-rose-100 text-rose-700 ring-1 ring-rose-200";
  if (s === "pending") return "bg-amber-100 text-amber-700 ring-1 ring-amber-200";
  return "bg-gray-100 text-gray-700 ring-1 ring-gray-200";
};

const downloadReceipt = (r: PayoutRow) => {
  const amount = Number(r.amount).toFixed(2);
  const created = (() => { try { return format(new Date(r.created_at), "yyyy-MM-dd HH:mm:ss"); } catch { return r.created_at; } })();
  const updated = (() => { try { return format(new Date(r.updated_at), "yyyy-MM-dd HH:mm:ss"); } catch { return r.updated_at; } })();
  const statusColor = r.status === "success" ? "#059669" : r.status === "failed" ? "#dc2626" : r.status === "pending" ? "#d97706" : "#374151";

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>Receipt - ${r.order_no}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Inter', 'Segoe UI', Roboto, system-ui, sans-serif; background: #f5f6fa; margin: 0; padding: 32px; color: #111827; }
  .card { max-width: 560px; margin: 0 auto; background: #fff; border-radius: 14px; box-shadow: 0 10px 30px rgba(0,0,0,.08); overflow: hidden; }
  .head { background: linear-gradient(135deg, #646ECD 0%, #4f56a8 100%); color: #fff; padding: 24px 28px; }
  .head h1 { margin: 0; font-size: 20px; letter-spacing: .3px; }
  .head p { margin: 4px 0 0; font-size: 12px; opacity: .85; }
  .body { padding: 24px 28px; }
  .amount { font-size: 32px; font-weight: 700; margin: 4px 0 18px; color: #111827; }
  .row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px dashed #e5e7eb; font-size: 13px; }
  .row:last-child { border-bottom: 0; }
  .row .k { color: #6b7280; }
  .row .v { color: #111827; font-weight: 500; font-family: 'JetBrains Mono', ui-monospace, Menlo, monospace; }
  .pill { display: inline-block; padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: ${statusColor}1a; color: ${statusColor}; text-transform: uppercase; }
  .foot { text-align: center; font-size: 11px; color: #9ca3af; padding: 14px 28px 22px; }
  .fail { background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c; padding: 10px 12px; border-radius: 8px; font-size: 12px; margin-top: 14px; }
  @media print { body { background: #fff; padding: 0; } .card { box-shadow: none; } .no-print { display: none; } }
  .actions { text-align: center; margin: 22px 0 0; }
  .btn { background: #646ECD; color: #fff; border: 0; padding: 10px 18px; border-radius: 8px; font-size: 13px; cursor: pointer; font-weight: 500; }
</style>
</head>
<body>
  <div class="card">
    <div class="head">
      <h1>Cash App Payout Receipt</h1>
      <p>Order No: ${r.order_no}</p>
    </div>
    <div class="body">
      <div style="font-size:12px;color:#6b7280;">Amount</div>
      <div class="amount">${amount} ${r.currency || "USD"}</div>
      <div class="row"><span class="k">Status</span><span class="pill">${r.status}</span></div>
      <div class="row"><span class="k">Account</span><span class="v">${r.account_no}</span></div>
      <div class="row"><span class="k">Currency</span><span class="v">${r.currency || "USD"}</span></div>
      <div class="row"><span class="k">Created</span><span class="v">${created}</span></div>
      <div class="row"><span class="k">Updated</span><span class="v">${updated}</span></div>
      ${r.failure_reason ? `<div class="fail"><strong>Failure Reason:</strong> ${r.failure_reason}</div>` : ""}
    </div>
    <div class="foot">This is a system-generated receipt. Keep it for your records.</div>
  </div>
  <div class="actions no-print">
    <button class="btn" onclick="window.print()">Print / Save as PDF</button>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `receipt_${r.order_no}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function CashAppPayout() {
  // ─── Role guard — SuperAdmin only ───────────────────────────────
  const userRole = localStorage.getItem("userRole") || "Guest";
  if (userRole !== "SuperAdmin") {
    return (
      <div className="p-4 ml-0 lg:ml-[256px]">
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg max-w-xl">
          <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
          <p className="text-sm">
            Cash App Payout sirf <strong>SuperAdmin</strong> role ke liye available hai.
            Aapka current role: <strong>{userRole}</strong>
          </p>
        </div>
      </div>
    );
  }

  return <CashAppPayoutInner />;
}

function CashAppPayoutInner() {
  // ─── Create form state ──────────────────────────────────────────
  const [amount, setAmount] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // ─── List state ─────────────────────────────────────────────────
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [load, setLoad] = useState(false);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  // ─── Fetch list ─────────────────────────────────────────────────
  const refetch = async () => {
    try {
      setLoad(true); setErr("");
      const qs = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(search ? { search } : {}),
      }).toString();

      const res = await fetch(`${CASHAPP_BASE}/payoutList?${qs}`, {
        headers: authHeaders(),
      });
      if (res.status === 401 || res.status === 403) {
        throw new Error("Unauthorized — SuperAdmin login required");
      }
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const json = await res.json();
      setRows(json.data || []);
      setTotal(json.total || 0);
    } catch (e: any) {
      setErr(e.message || "Server error");
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, [page, statusFilter]);

  // ─── Create payout ──────────────────────────────────────────────
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitMsg(null);

    if (!amount || !accountNo) {
      setSubmitMsg({ type: "error", text: "Amount aur Account No. dono required hain" });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${CASHAPP_BASE}/pursePay`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          amount: String(amount),
          accountNo: accountNo.trim(),
        }),
      });
      const json = await res.json();

      if (!res.ok || json.success === false) {
        const gatewayCode = json.respCode ? ` [code: ${json.respCode}]` : "";
        setSubmitMsg({
          type: "error",
          text: `${json.message || `Server error ${res.status}`}${gatewayCode}`,
        });
      } else {
        setSubmitMsg({
          type: "success",
          text: `Payout submitted — orderNo: ${json.orderNo} (status: ${json.status || "PENDING"})`,
        });
        setAmount("");
        setAccountNo("");
        setPage(1);
        refetch();
      }
    } catch (e: any) {
      setSubmitMsg({ type: "error", text: e.message || "Network error" });
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <div className="p-4 ml-0 lg:ml-[256px] space-y-6">
      <div className="flex items-center gap-4">
        <h1 className="text-lg font-semibold text-white">Cash App Payout</h1>
      </div>

      {/* Create form */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <h2 className="text-base font-semibold text-gray-800">Create new payout</h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Amount (USD)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="w-full p-2 border rounded-lg"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="e.g. 50"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Account No. ($cashtag)</label>
            <input
              type="text"
              className="w-full p-2 border rounded-lg"
              value={accountNo}
              onChange={e => setAccountNo(e.target.value)}
              placeholder="e.g. $johndoe"
              required
            />
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={submitting}
              className="w-full md:w-auto px-4 py-2 bg-[#646ECD] text-white rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {submitting ? "Sending..." : "Send Payout"}
            </button>
          </div>
        </form>

        {submitMsg && (
          <div className={`p-3 rounded text-sm ${
            submitMsg.type === "success" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"
          }`}>
            {submitMsg.text}
          </div>
        )}
      </div>

      {/* List header + filters */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-base font-semibold text-gray-800">Payout History</h2>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1 text-sm text-gray-500"
          >
            <RefreshCw className="w-4 h-4" /> reload
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select
            value={statusFilter}
            onChange={e => { setPage(1); setStatusFilter(e.target.value); }}
            className="p-2 border rounded-lg"
          >
            <option value="">All status</option>
            <option value="pending">Pending</option>
            <option value="success">Success</option>
            <option value="failed">Failed</option>
          </select>

          <input
            className="p-2 border rounded-lg"
            placeholder="Search by order_no / account_no"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { setPage(1); refetch(); } }}
          />

          <button
            onClick={() => { setPage(1); refetch(); }}
            className="px-4 py-2 bg-gray-200 rounded-md text-sm flex items-center justify-center gap-2"
          >
            <Search className="w-4 h-4" /> Search
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-indigo-50 to-violet-50">
              {["Order No", "Account", "Amount", "Currency", "Status", "Created", "Updated", "Receipt"].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-indigo-700 uppercase text-[11px] tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {load ? (
              <tr><td colSpan={8} className="py-6 text-center">Loading…</td></tr>
            ) : err ? (
              <tr><td colSpan={8} className="py-6 text-center text-red-600">{err}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="py-6 text-center text-gray-500">No payouts found</td></tr>
            ) : rows.map(r => (
              <tr key={r.id} className="border-t hover:bg-indigo-50/30 transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-gray-700">{r.order_no}</td>
                <td className="px-4 py-3 font-medium text-gray-800">{r.account_no}</td>
                <td className="px-4 py-3 font-mono font-semibold text-gray-900">${Number(r.amount).toFixed(2)}</td>
                <td className="px-4 py-3 text-gray-600 text-xs uppercase tracking-wide">{r.currency}</td>
                <td className="px-4 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wide ${statusClass(r.status)}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmt(r.created_at)}</td>
                <td className="px-4 py-3 text-xs text-gray-500">{fmt(r.updated_at)}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => downloadReceipt(r)}
                    title="Download receipt"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-[#646ECD] bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" /> Receipt
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex items-center justify-between px-4 py-3 border-t">
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="p-2 disabled:opacity-40"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600">
            Page {page} / {totalPages} · Total {total}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="p-2 disabled:opacity-40"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
